import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "state/hooks";
import { getGroupState } from "state/slices/group-slice";
import {
  getEventsPath,
  getEventWeightsPath,
} from "utils/backend-path-builders";
import { getRequest } from "utils/httpClient";
import {
  clearHomePageEventIdFilter,
  setHomePageEventIdFilter,
} from "state/slices/user-settings-slice";
import { EventsResponse } from "interfaces/events-response";
import shimmerStyles from "components/pool-details/entries/loading-bet-form/styles.module.scss";
import styles from "./featured-events-carousel.module.scss";
import { EventIdAndName } from "interfaces/event-id-and-name.ts";
import Carousel from "react-multi-carousel";

interface FeaturedEventsCarouselProps {
  eventFilter?: EventIdAndName;
  uniqueEvents: EventIdAndName[];
}

interface EventWeight {
  event_id: string;
  group_id: string;
  weight: number;
}

const FeaturedEventsCarousel: React.FC<FeaturedEventsCarouselProps> = ({
  eventFilter,
  uniqueEvents,
}) => {
  const dispatch = useAppDispatch();
  const groupId = useAppSelector(getGroupState).id;
  const [events, setEvents] = useState<EventsResponse[]>([]);
  const [eventWeights, setEventWeights] = useState<EventWeight[]>([]);
  const [loadedEventIds, setLoadedEventIds] = useState<string[]>([]);

  const imageCacheRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!groupId) return;
    getRequest(getEventWeightsPath(), {
      queryParams: { "group-id": groupId },
      skipIntegrationApi: true,
    })
      .then((data: EventWeight[]) => {
        setEventWeights(data || []);
      })
      .catch((error) => {
        console.error("Error fetching event weights:", error);
        setEventWeights([]);
      });
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !uniqueEvents || uniqueEvents.length === 0) {
      setEvents([]);
      return;
    }

    getRequest(getEventsPath(), {
      queryParams: { "group-id": groupId, sortBy: "weight:desc" },
      skipIntegrationApi: true,
    })
      .then((fetchedEvents: EventsResponse[]) => {
        const uniqueEventIds = new Set(uniqueEvents.map((ue) => ue.id));
        const eventsToShow = fetchedEvents.filter((event: EventsResponse) => {
          return uniqueEventIds.has(event.id) && event?.settings?.logo_url;
        });

        setEvents(eventsToShow);
      })
      .catch((error) => {
        console.error("Error fetching events:", error);
        setEvents([]);
      });
  }, [groupId, uniqueEvents]);

  const checkImageLoaded = (src: string, eventId: string) => {
    if (imageCacheRef.current.has(src)) {
      if (!loadedEventIds.includes(eventId)) {
        setLoadedEventIds((prevIds) => [...prevIds, eventId]);
      }
      return true;
    }
    return false;
  };

  const handleSelectEvent = (event: EventsResponse, isSelected: boolean) => {
    if (event == null || isSelected) {
      dispatch(clearHomePageEventIdFilter({ groupId: groupId }));
    } else {
      dispatch(
        setHomePageEventIdFilter({
          groupId: groupId,
          eventId: event.id,
        })
      );
    }
  };

  if (events.length === 0) {
    return <div></div>;
  }

  const eventsWithWeights = events.map((event: EventsResponse) => {
    const weight = eventWeights.find((ew) => ew.event_id === event.id)?.weight;
    return { ...event, weight };
  });

  const eventsWithPositiveWeight = eventsWithWeights.filter(
    (event) => event.weight && event.weight > 0
  );

  const responsive = {
    superLargeDesktop: {
      // the naming can be any, depends on you.
      breakpoint: { max: 4000, min: 2000 },
      items: 10,
    },
    largeDesktop: {
      breakpoint: { max: 2000, min: 1500 },
      items: 7,
    },
    desktop: {
      breakpoint: { max: 1500, min: 1000 },
      items: 5,
    },
    tablet: {
      breakpoint: { max: 1000, min: 464 },
      items: 4,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 3,
    },
  };
  return (
    <div style={{ width: "100%" }}>
      <Carousel
        swipeable={true}
        draggable={true}
        showDots={false}
        partialVisible={false}
        keyBoardControl={true}
        responsive={responsive}
        dotListClass={styles.dotList}
      >
        {eventsWithPositiveWeight.map((event: EventsResponse) => {
          if (!event.settings || !event.settings.logo_url) {
            return null;
          }

          const isSelected = eventFilter?.id === event.id;
          const isLoaded = loadedEventIds.includes(event.id);
          const logoUrl = event.settings.logo_url;

          const isCached = checkImageLoaded(logoUrl, event.id);

          return (
            <div
              key={event.id}
              className={styles.cardContainer}
              onClick={() => handleSelectEvent(event, isSelected)}
            >
              <div
                hidden={isLoaded || isCached}
                style={{
                  height: "100%",
                  borderRadius: 10,
                }}
                className={shimmerStyles.animatedBackground}
              />
              <img
                style={{
                  border: isSelected ? "2px solid white" : "",
                  display: isLoaded || isCached ? "block" : "none",
                  width: "100%",
                }}
                className={styles.image}
                hidden={!(isLoaded || isCached)}
                src={logoUrl}
                alt={event.name}
                onLoad={() => {
                  imageCacheRef.current.add(logoUrl);

                  if (!loadedEventIds.includes(event.id)) {
                    setLoadedEventIds((prevIds) => [...prevIds, event.id]);
                  }
                }}
                onError={() => {
                  console.warn(`Failed to load image for event: ${event.name}`);
                }}
              />
            </div>
          );
        })}
      </Carousel>
    </div>
  );
};

export default FeaturedEventsCarousel;
